// =============================================================================
// src/features/analytics/analytics-insights-markdown-blocks.ts - Scanner de
// BLOCOS do markdown das edicoes (linha a linha, sem lib externa — decisao de
// 2026-07-28: o subset do documento e fechado o bastante p/ scanner proprio).
// Blocos: heading / paragrafo / tabela GFM / blockquote / separador `---`.
// A conversao p/ HTML fica nos parsers de card/edicao (SRP).
// =============================================================================

/** Bloco cru do markdown, ainda sem conversao inline. */
export type AnalyticsInsightMarkdownBlock =
  | { readonly mdKind: 'heading'; readonly headingLevel: number; readonly headingText: string }
  | { readonly mdKind: 'paragraph'; readonly paragraphLines: readonly string[] }
  | {
      readonly mdKind: 'table';
      readonly headerCells: readonly string[];
      readonly rowsCells: readonly (readonly string[])[];
    }
  | { readonly mdKind: 'blockquote'; readonly quoteLines: readonly string[] }
  | { readonly mdKind: 'separator' };

/** Estado mutavel do scanner: acumuladores dos blocos multi-linha. */
interface AnalyticsInsightScanState {
  readonly scannedBlocks: AnalyticsInsightMarkdownBlock[];
  paragraphLines: string[];
  quoteLines: string[];
  /** true enquanto a tabela do topo de scannedBlocks aceita novas rows —
   * linha em branco fecha (duas tabelas vizinhas nunca se fundem). */
  tableOpen: boolean;
}

const REGEX_HEADING = /^(#{1,6})\s+(.*)$/;
const REGEX_TABLE_SEPARATOR_ROW = /^\|[\s:|-]+\|$/;

/** Divide uma linha `| a | b |` em celulas (pipes das bordas descartados). */
export function splitAnalyticsInsightTableRow(tableRowLine: string): string[] {
  return tableRowLine
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((tableCell) => tableCell.trim());
}

/** Scanner principal: markdown cru -> lista ordenada de blocos. */
export function scanAnalyticsInsightMarkdownBlocks(
  markdownBody: string,
): readonly AnalyticsInsightMarkdownBlock[] {
  const scanState: AnalyticsInsightScanState = {
    scannedBlocks: [],
    paragraphLines: [],
    quoteLines: [],
    tableOpen: false,
  };
  for (const rawLine of markdownBody.split('\n')) {
    scanAnalyticsInsightMarkdownLine(rawLine.trim(), scanState);
  }
  flushAnalyticsInsightAccumulators(scanState);
  return scanState.scannedBlocks;
}

function scanAnalyticsInsightMarkdownLine(trimmedLine: string, scanState: AnalyticsInsightScanState): void {
  if (trimmedLine === '') return flushAnalyticsInsightAccumulators(scanState);
  if (trimmedLine.startsWith('>')) {
    flushAnalyticsInsightParagraph(scanState);
    scanState.tableOpen = false;
    scanState.quoteLines.push(trimmedLine.replace(/^>\s?/, ''));
    return;
  }
  flushAnalyticsInsightQuote(scanState);
  if (trimmedLine.startsWith('|')) {
    flushAnalyticsInsightParagraph(scanState);
    appendAnalyticsInsightTableLine(trimmedLine, scanState);
    return;
  }
  scanState.tableOpen = false;
  scanAnalyticsInsightFlatLine(trimmedLine, scanState);
}

function scanAnalyticsInsightFlatLine(trimmedLine: string, scanState: AnalyticsInsightScanState): void {
  if (trimmedLine === '---') {
    flushAnalyticsInsightAccumulators(scanState);
    scanState.scannedBlocks.push({ mdKind: 'separator' });
    return;
  }
  const headingMatch = REGEX_HEADING.exec(trimmedLine);
  if (headingMatch?.[1] !== undefined && headingMatch[2] !== undefined) {
    flushAnalyticsInsightAccumulators(scanState);
    scanState.scannedBlocks.push({
      mdKind: 'heading',
      headingLevel: headingMatch[1].length,
      headingText: headingMatch[2].trim(),
    });
    return;
  }
  scanState.paragraphLines.push(trimmedLine);
}

/** 1a linha `|...|` = header; separador `|---|` ignorado; demais viram rows
 * enquanto a tabela estiver aberta (sem linha em branco no meio). */
function appendAnalyticsInsightTableLine(tableLine: string, scanState: AnalyticsInsightScanState): void {
  if (REGEX_TABLE_SEPARATOR_ROW.test(tableLine)) return;
  const rowCells = splitAnalyticsInsightTableRow(tableLine);
  const lastBlock = scanState.scannedBlocks[scanState.scannedBlocks.length - 1];
  if (scanState.tableOpen && lastBlock?.mdKind === 'table') {
    scanState.scannedBlocks[scanState.scannedBlocks.length - 1] = {
      mdKind: 'table',
      headerCells: lastBlock.headerCells,
      rowsCells: [...lastBlock.rowsCells, rowCells],
    };
    return;
  }
  scanState.scannedBlocks.push({ mdKind: 'table', headerCells: rowCells, rowsCells: [] });
  scanState.tableOpen = true;
}

function flushAnalyticsInsightParagraph(scanState: AnalyticsInsightScanState): void {
  if (scanState.paragraphLines.length === 0) return;
  scanState.scannedBlocks.push({ mdKind: 'paragraph', paragraphLines: [...scanState.paragraphLines] });
  scanState.paragraphLines = [];
}

function flushAnalyticsInsightQuote(scanState: AnalyticsInsightScanState): void {
  if (scanState.quoteLines.length === 0) return;
  scanState.scannedBlocks.push({ mdKind: 'blockquote', quoteLines: [...scanState.quoteLines] });
  scanState.quoteLines = [];
}

function flushAnalyticsInsightAccumulators(scanState: AnalyticsInsightScanState): void {
  flushAnalyticsInsightParagraph(scanState);
  flushAnalyticsInsightQuote(scanState);
  scanState.tableOpen = false;
}
